import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import { FaCalendarAlt, FaClock, FaUser, FaEye, FaArrowLeft } from "react-icons/fa";
import { fetchPublicBlogBySlug } from "../utils/blogApi";
import BlogCard from "./BlogCard";

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [jsonLd, setJsonLd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) {
      loadBlog();
    }
  }, [slug]);

  useEffect(() => {
    if (blog) {
      const pageTitle = blog.seo?.metaTitle || blog.title || "Blog | BBR Boat Rentals";
      const pageDescription = blog.seo?.metaDescription || blog.excerpt || "Read this article from BBR Boat Rentals.";
      const pageUrl = `${window.location.origin}/blog/${slug}`;
      const pageImage = blog.coverImage || `${window.location.origin}/assets/frontend/images/bbr-logo.png`;

      document.title = pageTitle;

      // Meta Description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.name = "description";
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute("content", pageDescription);

      // Meta Keywords (from tags)
      if (blog.tags && blog.tags.length > 0) {
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (!metaKeywords) {
          metaKeywords = document.createElement("meta");
          metaKeywords.name = "keywords";
          document.head.appendChild(metaKeywords);
        }
        metaKeywords.setAttribute("content", blog.tags.join(", "));
      }

      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = pageUrl;

      // Open Graph Tags
      const ogTags = [
        { property: "og:title", content: pageTitle },
        { property: "og:description", content: pageDescription },
        { property: "og:url", content: pageUrl },
        { property: "og:image", content: pageImage },
        { property: "og:type", content: "article" },
        { property: "og:site_name", content: "BBR Boat Rentals" },
        { property: "article:published_time", content: blog.publishedAt ? new Date(blog.publishedAt).toISOString() : "" },
        { property: "article:author", content: blog.author?.name || "BBR" },
      ];

      ogTags.forEach(({ property, content }) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement("meta");
          tag.setAttribute("property", property);
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
      });

      // Twitter Card Tags
      const twitterTags = [
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: pageTitle },
        { name: "twitter:description", content: pageDescription },
        { name: "twitter:image", content: pageImage },
      ];

      twitterTags.forEach(({ name, content }) => {
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (!tag) {
          tag = document.createElement("meta");
          tag.name = name;
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
      });

      // JSON-LD Structured Data
      const jsonLdData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: pageTitle,
        description: pageDescription,
        image: pageImage,
        url: pageUrl,
        datePublished: blog.publishedAt ? new Date(blog.publishedAt).toISOString() : "",
        dateModified: blog.updatedAt ? new Date(blog.updatedAt).toISOString() : "",
        author: {
          "@type": "Person",
          name: blog.author?.name || "BBR",
        },
        publisher: {
          "@type": "Organization",
          name: "BBR Boat Rentals",
          logo: {
            "@type": "ImageObject",
            url: `${window.location.origin}/assets/frontend/images/bbr-logo.png`,
          },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": pageUrl,
        },
        keywords: blog.tags ? blog.tags.join(", ") : "",
        articleSection: blog.type || "General",
        wordCount: blog.content ? blog.content.replace(/<[^>]*>/g, "").split(/\s+/).length : 0,
      };
      setJsonLd(jsonLdData);
    }

    return () => {
      // Cleanup meta tags on unmount
      const tagsToRemove = [
        'link[rel="canonical"]',
        'meta[name="keywords"]',
        'meta[property^="og:"]',
        'meta[property^="article:"]',
        'meta[name^="twitter:"]',
      ];
      tagsToRemove.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => el.remove());
      });
    };
  }, [blog, slug]);

  useEffect(() => {
    if (jsonLd) {
      let script = document.getElementById("blog-jsonld");
      if (!script) {
        script = document.createElement("script");
        script.id = "blog-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      const script = document.getElementById("blog-jsonld");
      if (script) script.remove();
    };
  }, [jsonLd]);

  const loadBlog = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPublicBlogBySlug(slug);
      if (data.success) {
        setBlog(data.blog);
        setRelatedPosts(data.relatedPosts || []);
      }
    } catch (err) {
      console.error("Error loading blog:", err);
      setError("Blog post not found.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="blog-page-loading">
        <Spinner animation="border" variant="dark" />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <Container className="blog-detail-error py-5 text-center">
        <h2>Blog Not Found</h2>
        <p>{error || "The blog post you're looking for doesn't exist."}</p>
        <Link to="/blog" className="btn btn-dark">
          ← Back to Blog
        </Link>
      </Container>
    );
  }

  const formattedDate = blog.publishedAt
    ? new Date(blog.publishedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <section className="blog-detail">
      <Container style={{ maxWidth: "1200px" }}>
        <div className="blog-detail-back">
          <Link to="/blog">
            <FaArrowLeft /> Back to Blog
          </Link>
        </div>

        <article className="blog-detail-article">
          <header className="blog-detail-header">
            <h1 className="blog-detail-title">{blog.title}</h1>
            <div className="blog-detail-meta">
              {formattedDate && (
                <span>
                  <FaCalendarAlt /> Updated on {formattedDate}
                </span>
              )}
              {blog.readingTime && (
                <span>
                  <FaClock /> {blog.readingTime}
                </span>
              )}
              {blog.author?.name && (
                <span>
                  <FaUser /> Written by {blog.author.name}
                </span>
              )}
              {blog.views > 0 && (
                <span>
                  <FaEye /> {blog.views} views
                </span>
              )}
            </div>
            {blog.tags && blog.tags.length > 0 && (
              <div className="blog-detail-tags">
                {blog.tags.map((tag, i) => (
                  <span key={i} className="blog-detail-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          {blog.coverImage && (
            <div className="blog-detail-cover">
              <img
                src={blog.coverImage}
                alt={blog.title}
                loading="lazy"
              />
            </div>
          )}

          <div
            className="blog-detail-content"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="blog-detail-related">
            <h2 className="blog-section-heading">Related Posts</h2>
            <Row className="blog-grid">
              {relatedPosts.map((post) => (
                <Col key={post.id} lg={3} md={6} sm={12} className="mb-4">
                  <BlogCard blog={post} />
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Container>
    </section>
  );
}
